import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, DollarSign, Repeat, Star, Building2, Handshake, CheckCircle, TrendingUp, Target, Zap, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ForAgentsPage() {
  const benefits = [
    {
      icon: Users,
      title: 'Access a Network of Serious Buyers',
      description: 'Connect with active investors who are ready to close — not tire-kickers. Our members are pre-qualified and looking for their next deal.'
    },
    {
      icon: Repeat,
      title: 'Build Repeat Business',
      description: 'Investors buy multiple properties per year. One relationship can turn into dozens of transactions over time.'
    },
    {
      icon: DollarSign,
      title: 'Higher Transaction Volume',
      description: 'Investor deals close faster, with fewer contingencies. Spend less time on each deal and close more per year.'
    },
    {
      icon: Star,
      title: 'Build Your Reputation',
      description: 'Collect reviews from satisfied investor clients. Your profile showcases your track record and expertise.'
    },
    {
      icon: Building2,
      title: 'Access Off-Market Inventory',
      description: 'See wholesale deals and off-market properties you can bring to your investor clients — before they hit the MLS.'
    },
    {
      icon: Handshake,
      title: 'Become the Go-To Agent',
      description: 'Position yourself as the investor-friendly realtor in your market. Investors talk — one great deal leads to referrals.'
    }
  ];

  const investorStats = [
    { value: '4.2', label: 'Deals/Year Average', sublabel: 'per active investor' },
    { value: '21', label: 'Days to Close', sublabel: 'average for investor deals' },
    { value: '67%', label: 'Cash Buyers', sublabel: 'no financing delays' }
  ];

  const idealFor = [
    'Realtors looking to work with investors',
    'Agents tired of slow residential transactions',
    'Brokers wanting to expand into investment sales',
    'New agents building a niche practice'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Real estate professional"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#5DADE2]/20 border border-[#5DADE2]/30 text-[#5DADE2] text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Briefcase className="h-4 w-4" />
              For Real Estate Agents & Brokers
            </span>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6" data-testid="agent-hero-title">
              Expand Your Buyer Network with Serious Investors
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Stop chasing unqualified leads. NextFlip connects you with active real estate investors who buy multiple properties per year — and close fast.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] text-white rounded-full px-8 text-lg h-14" data-testid="agent-signup-btn">
                <Link to="/signup">
                  Join as an Agent
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/directory">
                  View Provider Directory
                </Link>
              </Button>
            </div>

            <p className="text-slate-400 text-sm">
              Create your profile for free. Get discovered by investors in your market.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {investorStats.map((stat, i) => (
              <div key={i} className="p-6">
                <p className="text-5xl font-bold text-[#5DADE2] mb-2">{stat.value}</p>
                <p className="text-lg font-semibold text-white">{stat.label}</p>
                <p className="text-sm text-slate-400">{stat.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investor Demand Section */}
      <section className="py-24 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-amber-500/20 text-amber-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                High Demand Alert
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Investors Are Desperately Seeking Quality Agents
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                The #1 complaint we hear from investors? <span className="text-white font-semibold">"I can't find an agent who understands investment properties."</span> Most agents focus on traditional homebuyers and don't speak the language of cap rates, cash-on-cash returns, or 1031 exchanges.
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                That's your opportunity. Investors are actively searching for agents who get it — and they're willing to bring you <span className="text-[#5DADE2] font-semibold">repeat business, referrals, and faster closes</span>.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-3xl font-bold text-[#5DADE2] mb-1">73%</p>
                  <p className="text-sm text-slate-400">of investors say finding a good agent is their biggest challenge</p>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-3xl font-bold text-[#5DADE2] mb-1">89%</p>
                  <p className="text-sm text-slate-400">would pay more for an agent who understands investing</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-6">What Investors Are Saying:</h3>
              <div className="space-y-6">
                <blockquote className="border-l-4 border-[#5DADE2] pl-4">
                  <p className="text-slate-300 italic">"I've worked with 5 agents this year. Only one actually understood what I was looking for in a rental property."</p>
                  <cite className="text-sm text-slate-500 mt-2 block">— Cleveland Investor, 12 properties</cite>
                </blockquote>
                <blockquote className="border-l-4 border-[#5DADE2] pl-4">
                  <p className="text-slate-300 italic">"When I find an agent who gets it, I give them ALL my business. That's 4-6 deals a year."</p>
                  <cite className="text-sm text-slate-500 mt-2 block">— Out-of-State Investor</cite>
                </blockquote>
                <blockquote className="border-l-4 border-[#5DADE2] pl-4">
                  <p className="text-slate-300 italic">"I don't need hand-holding. I need an agent who can move fast and knows the numbers."</p>
                  <cite className="text-sm text-slate-500 mt-2 block">— Full-time Investor, 30+ units</cite>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Residential Buyers Are Exhausting. Investors Are Different.
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-500 font-bold">×</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Traditional buyers</p>
                    <p className="text-slate-600">Emotional decisions, financing issues, endless showings, and deals that fall through at the last minute.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="h-5 w-5 text-[#5DADE2]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Investor buyers</p>
                    <p className="text-slate-600">Numbers-driven, cash-ready, fast closings, minimal contingencies, and repeat business year after year.</p>
                  </div>
                </div>
              </div>
              <p className="mt-8 text-lg text-slate-700">
                One investor relationship can be worth <span className="font-semibold text-[#5DADE2]">10+ transactions</span> over your career. That's the power of working with NextFlip's network.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/8293700/pexels-photo-8293700.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Agent showing property"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Agents Love NextFlip
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Build a sustainable business with clients who buy consistently and refer others.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-[#5DADE2]/50 hover:shadow-lg transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-[#5DADE2] to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section className="py-20 bg-gradient-to-br from-[#5DADE2] to-blue-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Perfect For Ambitious Agents
              </h2>
              <div className="space-y-4">
                {idealFor.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-sky-200 flex-shrink-0" />
                    <span className="text-lg text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="font-heading text-2xl font-semibold text-white mb-6">
                Your Profile Includes:
              </h3>
              <ul className="space-y-3 text-white/90">
                <li className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-sky-200" />
                  Featured placement in investor searches
                </li>
                <li className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-sky-200" />
                  Client reviews and star ratings
                </li>
                <li className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-sky-200" />
                  Direct contact from serious buyers
                </li>
                <li className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-sky-200" />
                  Markets and specialties showcase
                </li>
                <li className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-sky-200" />
                  Access to off-market deal listings
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Get Started in 3 Simple Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Create Your Profile', desc: 'Sign up as a service provider and build your agent profile. Highlight your investor experience and markets.' },
              { step: '02', title: 'Get Discovered', desc: 'Investors searching for agents in your market will find you. Your reviews and track record build trust.' },
              { step: '03', title: 'Close More Deals', desc: 'Connect with serious buyers, access off-market listings, and build a pipeline of repeat clients.' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#5DADE2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-[#5DADE2]">{item.step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#5DADE2] to-blue-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Work with Serious Investors?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Create your free profile today and start connecting with active buyers in your market. No subscription fees — just results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-slate-100 rounded-full px-8 text-lg h-14">
              <Link to="/signup">
                Create Your Agent Profile
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 text-lg h-14">
              <Link to="/directory">
                See Other Agents
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
